import { WinePageContext } from '@context/WinePageContext/WinePageContext';
import { trackMasterWinePageView } from '@helpers/analytics';
import { AT, CZ, ES, FI } from '@helpers/countryHelpers';
import {
  GTM_ECOMMERCE_FIELD_NAMES,
  GTM_EVENT_NAMES,
  GTM_FIELD_NAMES,
  pushToDataLayer,
} from '@helpers/googleTagManager';
import { isVivinoPrice } from '@helpers/price';
import { getDisplayVintageYear } from '@vivino/js-web-common';
import { PriceType } from '@webtypes/common';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

import PurchaseAvailability from './PurchaseAvailability.container';

function findMerchantAndPriceWithPriceId(checkoutPrice, priceId) {
  const merchantsWithPrices = checkoutPrice?.merchants_with_prices || [];
  let price;
  let merchant;
  merchantsWithPrices.some((merchantPrice) => {
    price = merchantPrice?.prices.find((price) => price.id === priceId);
    if (price) {
      merchant = merchantPrice.merchant;
      return true;
    }
  });
  return [merchant, price];
}

const PurchaseAvailabilityList = ({
  wineId,
  vintage,
  className,
  countryCode,
  stateCode,
  valueForMoney,
  trackingScreen,
  merchantsWithPrices,
  priceAndAvailability,
  isVintageSubstitutionDisabled,
  checkoutPricesAndAvailabilities,
}) => {
  const { updateVintage, updatePageInformation, pageInformation } = useContext(WinePageContext);
  const [dropdownCheckoutPrices, setDropdownCheckoutPrices] = useState([]);
  const [hasTracked, setHasTracked] = useState(false);
  const [checkoutPrice, setCheckoutPrice] = useState({
    availability: null,
    market: null,
    price: pageInformation.price,
    merchant: pageInformation?.merchant,
    ...priceAndAvailability,
  });

  const createDropdownPrices = (checkoutPrices) => {
    return checkoutPrices.map((checkoutPrice) => ({
      element: checkoutPrice,
      name: getDisplayVintageYear(
        checkoutPrice.availability.vintage.year,
        vintage.wine?.vintage_type
      ),
      id: checkoutPrice.availability.vintage.id,
    }));
  };

  const handleSelectChange = ({ target }) => {
    const checkoutPriceElement = dropdownCheckoutPrices?.find(
      (item) => item.id.toString() === target.value.toString()
    )?.element;
    const newCheckoutPrice = { ...checkoutPriceElement };

    const availablePriceId = checkoutPriceElement?.availability?.price?.id;
    if (availablePriceId) {
      const [merchant, price] = findMerchantAndPriceWithPriceId(
        checkoutPriceElement,
        availablePriceId
      );

      newCheckoutPrice.merchant = merchant;
      newCheckoutPrice.price = price;
    }

    newCheckoutPrice.availability = checkoutPriceElement?.availability;
    newCheckoutPrice.market = checkoutPriceElement?.market;

    setCheckoutPrice(newCheckoutPrice);

    updatePageInformation({
      ...pageInformation,
      prices_and_availability: {
        ...pageInformation?.prices_and_availability,
        ...newCheckoutPrice,
      },
      vintage: {
        ...pageInformation?.vintage,
        ...newCheckoutPrice?.availability?.vintage,
      },
    });

    if (
      newCheckoutPrice.availability.vintage.year &&
      [AT, FI, CZ, ES].includes(String(countryCode).toUpperCase())
    ) {
      updateVintage({
        priceId: newCheckoutPrice.price?.id,
        year: newCheckoutPrice.availability.vintage.year,
      });
    }
  };

  const normalizePrices = ({ vintageCheckoutPrice = null, checkoutPrices = [] }) => {
    if (checkoutPrices.length === 0) {
      // If there are no prices stop loading
      return {
        dropdownCheckoutPrices: [],
        selectedDropdownCheckoutPrice: checkoutPrice,
      };
    }

    const checkoutPricesWithVivinoPrice = checkoutPrices.filter((checkoutPrice) =>
      isVivinoPrice(checkoutPrice.availability.price)
    );

    if (checkoutPricesWithVivinoPrice.length === 0) {
      /*
       * If there are no VC prices, use `vintageCheckoutPrice` (PPC price)
       * Otherwise, use the first price(PPC) for checkoutPrice.
       */
      return {
        dropdownCheckoutPrices: [],
        selectedDropdownCheckoutPrice: vintageCheckoutPrice || checkoutPrices[0],
      };
    }

    const dropdownCheckoutPrices = createDropdownPrices(checkoutPricesWithVivinoPrice);
    let selectedDropdownCheckoutPrice = null;
    const defaultVintageId = vintageCheckoutPrice?.availability?.vintage?.id;
    if (defaultVintageId) {
      selectedDropdownCheckoutPrice = dropdownCheckoutPrices.find(
        (dropdown) => dropdown.id === defaultVintageId
      );
    }
    if (!selectedDropdownCheckoutPrice) {
      selectedDropdownCheckoutPrice = dropdownCheckoutPrices[0];
    }

    const newCheckoutPrice = { ...selectedDropdownCheckoutPrice.element };

    const availablePriceId = vintageCheckoutPrice?.availability?.price?.id;
    if (availablePriceId) {
      const [merchant, price] = findMerchantAndPriceWithPriceId(
        vintageCheckoutPrice,
        availablePriceId
      );

      // Sometimes the merchant is not available in the vintage price, so we need to get it from the first available price
      const alternativeCheckoutPrice = merchantsWithPrices[0];

      newCheckoutPrice.merchant = merchant || alternativeCheckoutPrice?.merchant;
      newCheckoutPrice.price = price || alternativeCheckoutPrice?.prices[0];
    }

    newCheckoutPrice.availability = vintageCheckoutPrice?.availability;
    newCheckoutPrice.market = vintageCheckoutPrice?.market;

    return {
      dropdownCheckoutPrices,
      selectedDropdownCheckoutPrice: newCheckoutPrice,
    };
  };

  useEffect(() => {
    const { dropdownCheckoutPrices, selectedDropdownCheckoutPrice } = normalizePrices({
      checkoutPrices: checkoutPricesAndAvailabilities,
      vintageCheckoutPrice: priceAndAvailability,
    });

    if (dropdownCheckoutPrices.length > 0) {
      setDropdownCheckoutPrices(dropdownCheckoutPrices);
    }
    if (selectedDropdownCheckoutPrice) {
      setCheckoutPrice(selectedDropdownCheckoutPrice);
    }
  }, []);

  useEffect(() => {
    if (!hasTracked) {
      setHasTracked(true);

      const currentPrice = checkoutPrice?.price || checkoutPrice?.availability?.price;
      const [merchant, price] =
        findMerchantAndPriceWithPriceId(checkoutPrice, currentPrice?.id) || [];

      const priceId = price?.id;
      const priceType = price?.type;
      const priceAmount = price?.amount;

      trackMasterWinePageView({
        priceType,
        wineId: wineId,
        priceId: priceId,
        priceAmount: priceAmount,
        merchantId: merchant?.id,
        currencyCode: price?.currency?.code,
        wineryId: vintage?.wine?.winery,
        sponsored: vintage?.wine?.winery?.sponsored_entity_type,
        rating: vintage?.statistics?.ratings_average,
        buyButton: !!price,
      });
      if (priceType === PriceType.VC || priceType === PriceType.XDO) {
        // Clear ecommerce data
        pushToDataLayer({ [GTM_FIELD_NAMES.ECOMMERCE]: null });

        pushToDataLayer({
          price: price?.amount,
          [GTM_FIELD_NAMES.PRICE_ID]: priceId,
          [GTM_FIELD_NAMES.VINTAGE_ID]: vintage?.id,
          [GTM_FIELD_NAMES.CURRENCY_CODE]: price?.currency?.code,
          [GTM_FIELD_NAMES.EVENT]: GTM_EVENT_NAMES.VIEW_BUYABLE_WINE_PAGE,
          [GTM_FIELD_NAMES.ECOMMERCE]: {
            [GTM_ECOMMERCE_FIELD_NAMES.VALUE]: priceAmount,
            [GTM_ECOMMERCE_FIELD_NAMES.CURRENCY]: price?.currency?.code,
            [GTM_ECOMMERCE_FIELD_NAMES.ITEMS]: [
              {
                item_name: vintage?.name,
                item_brand: vintage?.wine?.winery?.name,
                item_id: priceId,
                item_category: 'wine',
                price: priceAmount,
                quantity: 1,
              },
            ],
          },
        });
      }
    }
  }, [hasTracked]);

  const pricesAndAvailability = normalizePrices({
    checkoutPrices: checkoutPricesAndAvailabilities,
    vintageCheckoutPrice: priceAndAvailability,
  });

  const { merchant, availability, market, price } =
    pricesAndAvailability.selectedDropdownCheckoutPrice;

  return (
    <PurchaseAvailability
      price={price}
      wineId={wineId}
      vintage={vintage}
      merchant={merchant}
      shouldTrack={false}
      className={className}
      stateCode={stateCode}
      countryCode={countryCode}
      marketInformation={market}
      availability={availability}
      valueForMoney={valueForMoney}
      trackingScreen={trackingScreen}
      onSelectChange={handleSelectChange}
      currency={merchant?.country?.currency}
      merchantsWithPrices={merchantsWithPrices}
      isVintageSubstitutionDisabled={isVintageSubstitutionDisabled}
      dropdownCheckoutPrices={pricesAndAvailability.dropdownCheckoutPrices}
    />
  );
};

PurchaseAvailabilityList.propTypes = {
  isDeal: PropTypes.bool,
  wineId: PropTypes.number,
  vintage: PropTypes.object,
  merchant: PropTypes.object,
  className: PropTypes.string,
  stateCode: PropTypes.string,
  countryCode: PropTypes.string,
  valueForMoney: PropTypes.object,
  trackingScreen: PropTypes.string,
  merchantsWithPrices: PropTypes.array,
  priceAndAvailability: PropTypes.object,
  merchantShippingMessage: PropTypes.string,
  isVintageSubstitutionDisabled: PropTypes.bool,
  checkoutPricesAndAvailabilities: PropTypes.array,
};

export default PurchaseAvailabilityList;
